import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Home, BarChart3, Users, Eye, Download, BookOpen, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const Analytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    const load = async () => {
      try {
        const { data } = await analyticsAPI.overview();
        setData(data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, navigate]);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C53030]" />
      </div>
    );
  }

  const stats = [
    { label: 'Total Plans', value: data.summary.total_plans, icon: BookOpen, testid: 'stat-total-plans' },
    { label: 'Total Users', value: data.summary.total_users, icon: Users, testid: 'stat-total-users' },
    { label: 'Active (7d)', value: data.summary.active_users_7d, icon: TrendingUp, testid: 'stat-active-users' },
    { label: 'Library Plans', value: data.summary.library_plans, icon: BarChart3, testid: 'stat-library-plans' },
    { label: 'Total Views', value: data.summary.total_views, icon: Eye, testid: 'stat-total-views' },
    { label: 'Downloads', value: data.summary.total_downloads, icon: Download, testid: 'stat-total-downloads' },
  ];

  const maxWeekly = Math.max(1, ...data.weekly_trends.map((w) => w.plans_created));

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button data-testid="home-button" variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#C53030]">
            <Home className="w-5 h-5 mr-2" /> Home
          </Button>
          <div className="flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#C53030]" />
            <h1 className="text-xl font-bold text-[#1C1917]">Admin Analytics</h1>
          </div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-10">
        <section>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#1C1917] mb-6">Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {stats.map((s) => (
              <Card key={s.label} data-testid={s.testid} className="p-5 border border-stone-200">
                <s.icon className="w-5 h-5 text-[#C53030] mb-2" />
                <p className="text-2xl font-bold text-[#1C1917]">{s.value}</p>
                <p className="text-xs text-stone-500 mt-1">{s.label}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card data-testid="chart-by-class" className="p-6 border border-stone-200">
            <h3 className="text-lg font-semibold text-[#1C1917] mb-4">Plans by Class</h3>
            <BarList items={data.plans_by_class.map((r) => ({ label: r.class_name, value: r.count }))} />
          </Card>
          <Card data-testid="chart-by-subject" className="p-6 border border-stone-200">
            <h3 className="text-lg font-semibold text-[#1C1917] mb-4">Plans by Subject</h3>
            <BarList items={data.plans_by_subject.map((r) => ({ label: r.subject, value: r.count }))} />
          </Card>
        </section>

        <section>
          <Card data-testid="chart-weekly-trends" className="p-6 border border-stone-200">
            <h3 className="text-lg font-semibold text-[#1C1917] mb-4">Weekly Trends (last 8 weeks)</h3>
            {data.weekly_trends.length === 0 ? (
              <p className="text-sm text-stone-500">No data yet.</p>
            ) : (
              <div className="flex items-end gap-3 h-40">
                {data.weekly_trends.map((w) => (
                  <div key={w.week} className="flex-1 flex flex-col items-center justify-end gap-1">
                    <div
                      className="w-full bg-[#C53030] rounded-t"
                      style={{ height: `${(w.plans_created / maxWeekly) * 100}%` }}
                      title={`${w.plans_created} plans`}
                    />
                    <span className="text-[10px] text-stone-500">{w.week.split('-W')[1]}w</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </section>

        <section>
          <Card data-testid="top-chapters-table" className="p-6 border border-stone-200">
            <h3 className="text-lg font-semibold text-[#1C1917] mb-4">Top Chapters</h3>
            {data.top_chapters.length === 0 ? (
              <p className="text-sm text-stone-500">No engagement yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-stone-500 border-b border-stone-200">
                    <th className="py-2">Chapter</th>
                    <th className="py-2">Subject</th>
                    <th className="py-2">Class</th>
                    <th className="py-2 text-right">Views</th>
                    <th className="py-2 text-right">Downloads</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_chapters.map((c, i) => (
                    <tr key={`top-chapter-${c.chapter}-${i}`} className="border-b border-stone-100">
                      <td className="py-2 font-medium text-[#1C1917]">{c.chapter}</td>
                      <td className="py-2 text-stone-600">{c.subject}</td>
                      <td className="py-2 text-stone-600">{c.class_name}</td>
                      <td className="py-2 text-right">{c.views}</td>
                      <td className="py-2 text-right">{c.downloads}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </section>
      </main>
    </div>
  );
};

const BarList = ({ items }) => {
  if (!items.length) return <p className="text-sm text-stone-500">No data yet.</p>;
  const max = Math.max(...items.map((i) => i.value));
  return (
    <div className="space-y-2">
      {items.map((it) => (
        <div key={it.label} className="flex items-center gap-3">
          <span className="text-sm text-stone-700 w-32 truncate">{it.label}</span>
          <div className="flex-1 bg-stone-100 rounded h-6 relative overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#C53030] to-[#9B2C2C] rounded"
              style={{ width: `${(it.value / max) * 100}%` }}
            />
          </div>
          <span className="text-sm font-semibold text-[#1C1917] w-8 text-right">{it.value}</span>
        </div>
      ))}
    </div>
  );
};

export default Analytics;
